import { createDefaultPrinterConfig } from '@/services/printers/printerConfig';
import {
  getGenericEscposPrintersConfig,
  sendToGenericEscposPrinterByDuty,
} from '@/services/printers/genericEscpos';
import {
  getUsbAndroidPrintersConfig,
  sendToUsbAndroidPrinterByDuty,
} from '@/services/printers/usbAndroid';
import {
  getUsbWindowsPrintersConfig,
  sendToUsbWindowsPrinterByDuty,
} from '@/services/printers/usbWindows';
import {
  OPEN_CASH_DRAWER_TEMPLATE_TYPE,
  PrinterBackend,
  PrinterConfig,
  PrinterDuty,
  PrinterTemplate,
} from '@/services/printers/types';
import {
  isCashierDutyReady,
  resolveBackendForDuty,
  resolveBackendsForDuty,
} from '@/services/printers/printerRouting';

const sendToBackendByDuty = async (
  backend: PrinterBackend,
  template: PrinterTemplate,
  duty: PrinterDuty
): Promise<boolean> => {
  if (backend === 'usb_android') {
    return sendToUsbAndroidPrinterByDuty(template, duty);
  }
  if (backend === 'usb_windows') {
    return sendToUsbWindowsPrinterByDuty(template, duty);
  }
  return sendToGenericEscposPrinterByDuty(template, duty);
};

export const printTemplateByDuty = async (
  template: PrinterTemplate,
  duty: PrinterDuty
): Promise<boolean> => {
  const backends = resolveBackendsForDuty(duty);
  if (backends.length === 0) {
    console.warn(`No printer backend configured for duty: ${duty}`);
    return false;
  }

  let success = false;
  for (const backend of backends) {
    const result = await sendToBackendByDuty(backend, template, duty);
    if (result) success = true;
  }

  return success;
};

export const hasPrinterForDuty = (duty: PrinterDuty): boolean => {
  return resolveBackendForDuty(duty) !== null;
};

export const getCashierPrinterConfig = (): PrinterConfig | null => {
  const backend = resolveBackendForDuty('cashier');
  if (!backend) return null;

  if (backend === 'generic_escpos') {
    const genericConfig = getGenericEscposPrintersConfig();
    const genericCashier = genericConfig.printers.find((printer) => printer.duties.includes('cashier'));
    if (!genericCashier) return null;

    return createDefaultPrinterConfig({
      name: genericCashier.name,
      duties: genericCashier.duties,
      ip: genericCashier.host,
      port: genericCashier.port,
      timeout: genericCashier.timeout,
      language: genericCashier.language,
      currency: genericCashier.currency,
    });
  }

  if (backend === 'usb_android') {
    const usbConfig = getUsbAndroidPrintersConfig();
    const usbCashier = usbConfig.printers.find((printer) => printer.duties.includes('cashier'));
    if (!usbCashier) return null;

    return createDefaultPrinterConfig({
      name: usbCashier.name,
      duties: usbCashier.duties,
      language: usbCashier.language,
      currency: usbCashier.currency,
    });
  }

  if (backend === 'usb_windows') {
    const usbConfig = getUsbWindowsPrintersConfig();
    const usbCashier = usbConfig.printers.find((printer) => printer.duties.includes('cashier'));
    if (!usbCashier) return null;

    return createDefaultPrinterConfig({
      name: usbCashier.name,
      duties: usbCashier.duties,
      language: usbCashier.language,
      currency: usbCashier.currency,
    });
  }

  return null;
};

export const isCashierPrinterAvailableOnDevice = (): boolean => {
  return isCashierDutyReady();
};

export const openCashierDrawer = async (): Promise<void> => {
  if (!isCashierPrinterAvailableOnDevice()) {
    const error = new Error('No cashier printer available on this device');
    error.name = 'NoCashierPrinter';
    throw error;
  }

  const template: PrinterTemplate = {
    commands: [{ method: 'addPulse', params: [] }],
    metadata: {
      type: OPEN_CASH_DRAWER_TEMPLATE_TYPE,
      timestamp: new Date(),
    },
  };

  const opened = await printTemplateByDuty(template, 'cashier');
  if (!opened) {
    const error = new Error('Failed to open the register');
    error.name = 'OpenRegisterFailed';
    throw error;
  }
};

export const printCashierThermalTicket = async (
  buildTemplate: (config: PrinterConfig) => PrinterTemplate,
  currency?: string,
): Promise<void> => {
  if (!isCashierPrinterAvailableOnDevice()) {
    console.warn('No cashier printer available on this device, skipping thermal ticket print');
    return;
  }

  try {
    const cashierPrinter = getCashierPrinterConfig();
    if (!cashierPrinter) {
      console.warn('No cashier printer configured, skipping thermal ticket print');
      return;
    }

    const printerConfig = currency
      ? { ...cashierPrinter, currency }
      : cashierPrinter;

    const template = buildTemplate(printerConfig);
    const printed = await printTemplateByDuty(template, 'cashier');
    if (!printed) {
      console.warn('Failed to send thermal ticket to printer');
    }
  } catch (error) {
    console.warn('Thermal printer failed', error);
  }
};

export { backendHasPrinterForDuty, resolveBackendForDuty } from '@/services/printers/printerRouting';
